<script>
// Imports
import { AsYouType } from 'libphonenumber-js'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { phoneNumber } from '@/utils/validators'

import { logIn } from '@/auth'
import { sendVerificationCode, verifyCode } from '@/graph/mutations/user.js'
import ConfirmationCodeInput from '@/components/signup/ConfirmationCodeInput.vue'

export default {
  name: 'LoginView',
  components: { ConfirmationCodeInput },
  mixins: [validationMixin],
  data() {
    return {
      // Signup Data
      form: {
        countryCode: '60',
        phone: ''
      },
      code: '',

      verifyingCode: false,
      sendingCode: false,
      codeSent: false,
      resendAvailable: false,

      sendingErrors: null,
      verificationErrors: null
    }
  },
  computed: {
    formattedPhone() {
      const { countryCode, phone } = this.form
      return new AsYouType().input('+' + countryCode + phone)
    }
  },
  methods: {
    goToSendCodePage() {
      if (this.sendingCode) return

      this.sendingErrors = null
      this.verificationErrors = null

      this.codeSent = false
      this.code = ''
    },

    // Send code
    sendCode() {
      if (this.$v.$invalid) return
      this.sendingErrors = null
      this.sendingCode = true
      this.verifyingCode = false
      this.resendAvailable = false

      sendVerificationCode(this.formattedPhone)
        .then(() => {
          this.codeSent = true

          setTimeout(() => {
            this.resendAvailable = true
          }, 30000)
        })
        .catch(err => (this.sendingErrors = err.graphQLErrors))
        .finally(() => (this.sendingCode = false))
    },

    verifyCode() {
      if (this.verifyingCode) return
      this.verificationErrors = null
      this.verifyingCode = true

      const target = this.$isSeller ? 'SELLER' : 'BUYER'

      verifyCode({ phone: this.formattedPhone, code: this.code, target })
        .then(res => {
          logIn(res.data.checkVerificationCode)
          this.$store.dispatch('user/logIn')

          // if (this.$isBuyer) {
          // this.$router.replace('/home')
          // } else {
          this.$router.replace({ name: 'ProfileOnboardingView' })
          // }
        })
        .catch(err => {
          this.code = ''
          this.verificationErrors = err.graphQLErrors
        })
        .finally(() => (this.verifyingCode = false))
    },

    checkCode() {
      this.verificationErrors = null
      if (this.code.length === 4) {
        this.verifyCode()
      }
    }
  },
  validations: {
    form: {
      countryCode: { required },
      phone: { required }
    },
    formattedPhone: {
      phoneNumber
    }
  }
}
</script>

<template>
  <v-container ma-0 pa-0>
    <!-- Log in view -->
    <transition class="slide-up" appear>
      <div v-if="!codeSent" class="view-grid">
        <div class="hero-wrapper mt-5">
          <v-img
            v-if="$isSeller"
            src="@/assets/hero_seller.png"
            class="hero-seller"
          />
          <v-img v-else src="@/assets/hero_buyer.png" class="hero-buyer" />
        </div>
        <div class="content-bottom mb-3" align-end justify-center>
          <!-- Send code page -->
          <v-form @submit.prevent="sendCode()">
            <v-container pb-0>
              <!-- Title -->
              <h2 v-if="$isSeller" class="display-1 mb-2 accent--text">
                Let’s get started
              </h2>
              <div v-else class="display-1 mb-3 accent--text">
                Home-cooked food, without the hassle
              </div>

              <!-- Subtitle -->
              <p v-if="$isSeller" class="primary--text">
                To sell your home cooked food on Pona, please enter your phone
                number below.
              </p>
              <p v-else class="primary--text">
                Order delicious take-out meals prepared with care by home chefs
                within your community.
              </p>

              <div class="phone-field d-flex justify-center">
                <v-text-field
                  v-model="form.countryCode"
                  color="blue-grey"
                  prefix="+"
                  value="372"
                  required
                  class="country-code-input mr-4"
                  type="tel"
                />
                <v-text-field
                  ref="phone"
                  v-model="form.phone"
                  :autofocus="true"
                  :persistent-hint="true"
                  color="blue-grey"
                  label="Your phone number"
                  type="tel"
                />
              </div>

              <div v-if="sendingErrors" class="v-messages error--text mb-3">
                <div
                  v-for="(error, i) in sendingErrors"
                  :key="i"
                  class="v-messages__message"
                >
                  {{ error.message }}
                </div>
              </div>

              <div class="mt-2 xs6">
                <v-btn
                  :loading="sendingCode"
                  :disabled="$v.$invalid"
                  type="submit"
                  rounded
                  depressed
                  color="primary"
                >
                  Continue
                </v-btn>
              </div>
            </v-container>
          </v-form>
        </div>
      </div>
    </transition>

    <transition class="slide-up" appear>
      <v-layout v-if="codeSent" mt-12 pa-5>
        <!-- AppBar -->
        <v-app-bar color="white" flat app>
          <v-btn icon @click="goToSendCodePage()">
            <v-icon color="black" size="18">$vuetify.icons.arrowBack</v-icon>
          </v-btn>
        </v-app-bar>

        <!-- Confirm code page -->
        <v-form @submit.prevent="checkCode()">
          <v-container pa-0>
            <div class="display-1 font-weight-bold mb-3">
              Confirmation
            </div>
            <v-container class="px-0">
              <p class="subheading mb-6">
                Enter the 4-digit code sent to you at
                <span class="primary--text text-no-wrap">
                  {{ formattedPhone }}
                </span>
              </p>

              <ConfirmationCodeInput v-model="code" @input="checkCode" />

              <!-- Display both sending & verification errors -->
              <div
                v-if="verificationErrors || sendingErrors"
                class="v-messages error--text mb-2"
              >
                <div
                  v-for="(err, i) in [...verificationErrors, ...sendingErrors]"
                  :key="i"
                  class="v-messages__message"
                >
                  {{ err ? err.message : '' }}
                </div>
              </div>

              <v-btn
                :loading="verifyingCode"
                :disabled="code.length < 4 || verifyingCode"
                type="submit"
                rounded
                depressed
                color="primary"
                class="mt-2"
              >
                Submit
              </v-btn>

              <v-btn
                v-if="resendAvailable"
                :loading="sendingCode"
                type="button"
                rounded
                outlined
                depressed
                color="secondary"
                class="mt-2 ml-2"
                @click="sendCode()"
              >
                <v-icon
                  size="16"
                  color="secondary"
                  left
                  v-text="'$vuetify.icons.redo'"
                />
                Resend
              </v-btn>
            </v-container>
          </v-container>
        </v-form>
      </v-layout>
    </transition>
  </v-container>
</template>

<style lang="sass" scoped>
.display-1
  font-family: 'MetropolisBold', sans-serif !important
  font-weight: 800
  font-size: 1.85rem !important
  line-height: 2rem !important

.subheading
  font-size: 1.1rem

button[type="submit"]
  min-width: 88px !important

.phone-field::v-deep input
  padding-right: 3px
  padding-left: 3px
  font-size: 1.1rem
  font-weight: 500

.country-code-input
  width: 68px
  flex-shrink: 0
  flex-grow: 0
  &::v-deep .v-input__prepend-outer
    margin: 8px 5px 8px 0
    .v-input__icon
      height: 16px
      min-width: 16px
      width: 16px
      .v-icon
        font-size: 16px !important

.home
  height: 100vh
  padding: 8px
  background: white

.hero-wrapper
  max-width: 500px
  margin: 0 auto
</style>
